import type React from "react";
import {
  type JSXElementConstructor,
  type ReactElement,
  cloneElement,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { Button, Form, Label } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { AccessibleModal } from "simplydo/react/components/Accessibility/AccessibleModal";

import util from "utils/utils";

import Tag, { TagContainer, type TagProps } from "components/lib/Tag";

type TagViewProps = {
  tags: OpenAPI.Schemas["Tag"][];
  hideView?: boolean;
  noMargin?: boolean;
  children?: React.ReactNode;
} & Omit<TagProps, "tag">;

export const TagView = ({
  tags,
  hideView,
  noMargin,
  children,
  ...tagProps
}: TagViewProps) =>
  hideView ? children : (
    <TagContainer $noMargin={noMargin}>
      {tags ? tags.map((t) => <Tag tag={t} key={t._id} {...tagProps} />) : null}
      {children}
    </TagContainer>
  );

const TagChooserContent = ({ tags, allTags, removeTag, addTag, showCount }) => {
  const tagIds = useMemo(() => tags.map((c) => c._id), [tags]);
  const { t } = useTranslation();
  const { selectedTags = [], availableTags = [] } = allTags.reduce((p, c) => {
    p.selectedTags = p.selectedTags || [];
    p.availableTags = p.availableTags || [];

    if (tagIds.includes(c._id)) {
      p.selectedTags.push(c);
    } else {
      p.availableTags.push(c);
    }
    return p;
  }, {});
  return (
    <Form>
      {tags?.length > 0 && <h5>Selected tags</h5>}
      <TagView
        tags={selectedTags}
        showCount={showCount}
        noRedirect
        onClick={(t) => {
          removeTag(t);
        }}
        onRemove={(t) => {
          removeTag(t);
        }}
      />
      <h5>{t("generic.tags")}</h5>
      <p style={{ marginBottom: 0 }}>{t("tags.click")}</p>
      <TagView
        tags={availableTags}
        noRedirect
        showCount={showCount}
        onClick={(t) => {
          if (!tagIds.includes(t._id)) {
            addTag(t);
          }
        }}
      />
    </Form>
  );
};

type TagModalChooserProps = {
  tags: OpenAPI.Schemas["Tag"][];
  availableTags?: OpenAPI.Schemas["Tag"][];
  removeTag?: (tag: OpenAPI.Schemas["Tag"]) => void;
  addTag?: (tag: OpenAPI.Schemas["Tag"]) => void;
  trigger?: ReactElement<any, string | JSXElementConstructor<any>>;
  noRedirect?: boolean;
  canEdit?: boolean;
  showCount?: boolean;
  viewCanRemove?: boolean;
  onStateChange?: (state: boolean) => void;
  hideView?: boolean;
  noMargin?: boolean;
  isChallenge?: boolean;
};

const TagModalChooser = ({
  tags = [],
  availableTags = [],
  removeTag,
  addTag,
  trigger,
  noRedirect,
  canEdit = true,
  showCount = true,
  viewCanRemove,
  onStateChange,
  hideView = false,
  noMargin,
  isChallenge,
}: TagModalChooserProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const triggerElement = useMemo(
    () =>
      trigger ?
        cloneElement(trigger, {
          onClick: () => {
            onStateChange?.(true);
            setModalOpen(true);
          },
        })
      : null,
    [trigger, onStateChange],
  );
  const allTags = availableTags || [];

  if (!canEdit) {
    return <TagView tags={tags} />;
  }

  if (!trigger) {
    return (
      <TagChooserContent
        tags={tags}
        allTags={allTags}
        removeTag={removeTag}
        addTag={addTag}
        showCount={showCount}
      />
    );
  }

  if (availableTags.length === 0) {
    return null;
  }
  return (
    <>
      <TagView
        tags={tags}
        noRedirect={noRedirect}
        onRemove={viewCanRemove ? removeTag : null}
        hideView={hideView}
        noMargin={noMargin}
      >
        {triggerElement}
      </TagView>

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={modalOpen}
        closeOnDimmerClick
        onClose={() => {
          onStateChange?.(false);
          setModalOpen(false);
        }}
      >
        <AccessibleModal.Header>{t("generic.tags")}</AccessibleModal.Header>
        <AccessibleModal.Content>
          <p>
            {t("tags.select")}{" "}
            {isChallenge ? t("generic.challenge") : `${t("generic.idea")}`}.
          </p>
          <TagChooserContent
            tags={tags}
            allTags={allTags}
            removeTag={removeTag}
            addTag={addTag}
            showCount={showCount}
          />
        </AccessibleModal.Content>
        <AccessibleModal.Actions>
          <Button
            primary
            onClick={() => {
              onStateChange?.(false);
              setModalOpen(false);
            }}
          >
            {t("generic.done")}
          </Button>
        </AccessibleModal.Actions>
      </AccessibleModal>
    </>
  );
};

export default TagModalChooser;
